/**
 * Auth Provider for Hono API
 */

import type { AuthProvider } from '@refinedev/core';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:8787';

export const authProvider: AuthProvider = {
  login: async ({ password }) => {
    try {
      const response = await fetch(`${API_URL}/auth/login`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ password }),
      });

      if (response.ok) {
        return { success: true, redirectTo: '/' };
      }

      // Handle rate limiting
      if (response.status === 429) {
        const retryAfter = response.headers.get('Retry-After');
        const error = await response.json();
        return {
          success: false,
          error: {
            message: error.error?.message || `Too many attempts. Try again in ${retryAfter || '30'} seconds.`,
            name: 'RateLimitError',
          },
        };
      }

      const error = await response.json();
      return {
        success: false,
        error: {
          message: error.error?.message || 'Invalid password',
          name: error.error?.code === 'INVALID_PASSWORD' ? 'InvalidCredentials' : 'LoginError',
        },
      };
    } catch (error) {
      return {
        success: false,
        error: {
          message: 'Network error. Please check your connection.',
          name: 'NetworkError',
        },
      };
    }
  },

  logout: async () => {
    try {
      await fetch(`${API_URL}/auth/logout`, {
        method: 'POST',
        credentials: 'include',
      });
    } catch {
      // Ignore logout errors
    }
    return { success: true, redirectTo: '/login' };
  },

  check: async () => {
    try {
      const response = await fetch(`${API_URL}/auth/me`, {
        credentials: 'include',
      });

      if (response.ok) {
        const data = await response.json();
        if (data.authenticated) {
          return { authenticated: true };
        }
      }

      return { authenticated: false, redirectTo: '/login' };
    } catch {
      return { authenticated: false, redirectTo: '/login' };
    }
  },

  getIdentity: async () => {
    return {
      id: 'admin',
      name: 'Administrator',
      avatar: undefined,
    };
  },

  onError: async (error) => {
    if (error.status === 401) {
      return { logout: true, redirectTo: '/login' };
    }
    return { error };
  },

  getPermissions: async () => {
    return ['admin'];
  },
};
